import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDiscovery } from '../context/DiscoveryContext';
import { IndustrySelectStep } from './IndustrySelectStep';
import { SubcategorySelectStep } from './SubcategorySelectStep';
import { GenieRecommendationsStep } from './GenieRecommendationsStep';
import { cn } from '@/lib/utils';
import { ArrowLeft, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEventOnce } from '@/lib/ga4';

interface DiscoveryWizardProps {
  className?: string;
  onClose?: () => void;
}

const steps = [
  { id: 'industry', label: 'Industry' },
  { id: 'subcategory', label: 'Business' },
  { id: 'genies', label: 'Genies' },
];

export const DiscoveryWizard = ({ className, onClose }: DiscoveryWizardProps) => {
  const navigate = useNavigate();
  const { state, goBack } = useDiscovery();
  const currentStepIndex = steps.findIndex(s => s.id === state.step);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [state.step]);

  // Fire once when the Discover wizard first mounts (HG-0791), tagged with UTMs.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const params: Record<string, string> = {};
    const source = p.get('utm_source');
    const campaign = p.get('utm_campaign');
    if (source) params.traffic_source = source;
    if (campaign) params.campaign = campaign;
    trackEventOnce('discover_start', 'discover_start', params);
  }, []);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/');
    }
  };

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Top bar with logo, progress, and close */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        {/* Logo / Back button */}
        {currentStepIndex > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={goBack}
            className="text-white/70 hover:text-white hover:bg-white/10 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </Button>
        ) : (
          <a href="/" className="flex items-center gap-2">
            <img
              src="/platform-icons/helpgenie_logo.png"
              alt="Help Genie"
              className="w-8 h-8"
            />
            <span className="text-white font-semibold hidden sm:block">Help Genie</span>
          </a>
        )}

        {/* Progress indicator - centered */}
        <div className="flex items-center gap-1 sm:gap-2">
          {steps.map((step, index) => {
            const isActive = state.step === step.id;
            const isCompleted = index < currentStepIndex;

            return (
              <div key={step.id} className="flex items-center">
                {/* Step circle */}
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all duration-300',
                      isActive
                        ? 'bg-white text-purple-600 shadow-lg shadow-purple-500/30'
                        : isCompleted
                          ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                          : 'bg-white/20 text-white/60'
                    )}
                  >
                    {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                  <span
                    className={cn(
                      'mt-1 text-[10px] sm:text-xs font-medium transition-colors hidden sm:block',
                      isActive ? 'text-white' : isCompleted ? 'text-white/80' : 'text-white/40'
                    )}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'w-8 sm:w-12 h-0.5 mx-1 sm:mx-2 rounded-full transition-colors',
                      index < currentStepIndex ? 'bg-gradient-to-r from-purple-500 to-blue-500' : 'bg-white/20'
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="text-white/70 hover:text-white hover:bg-white/10"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Main content area - takes remaining height */}
      <div ref={contentRef} className="flex-1 overflow-y-auto">
        <div className="min-h-full flex items-start sm:items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className={cn('w-full', state.step === 'genies' ? 'max-w-[1400px]' : 'max-w-6xl')}>
            {state.step === 'industry' && <IndustrySelectStep />}
            {state.step === 'subcategory' && <SubcategorySelectStep />}
            {state.step === 'genies' && <GenieRecommendationsStep />}
          </div>
        </div>
      </div>
    </div>
  );
};
